import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  IBankTransferItem,
  IBranchScopeOptions,
  ICashDisbursementItem,
  ICreateRunRequest,
  IFinalSettlementBatchResult,
  IFinalSettlementDetail,
  IGetScopeEmployeesParams,
  IJobLog,
  IListItemsParams,
  IListPaymentFilesParams,
  IListPayrollHistoryParams,
  IListPayslipsParams,
  IListRunsParams,
  IPaymentFile,
  IPaymentFileMonthGroup,
  IPayrollAuditLog,
  IPayslipConfigPreview,
  IPayrollBranch,
  IPayrollHistoryMeta,
  IPayrollHistoryMonth,
  IPayrollRun,
  IPayrollRunDetail,
  IPayrollRunItem,
  IPayrollRunItemDetail,
  IPayslipListItem,
  IPayslipStats,
  IPeriodOptionsParams,
  IPeriodOptionsResponse,
  IReprocessHeldResult,
  IRunProgress,
  IScopeEmployee,
  IUpdateItemRequest,
  IUpdateRunRequest,
  IWpsExcludedResponse,
  IBankDetailsSaveResult,
  IAmountsSaveResult,
} from "@/types/(company)/payrollRuns.interface";

export type {
  IBankTransferItem,
  IBranchScopeOptions,
  ICashDisbursementItem,
  ICreateRunRequest,
  ICurrencyTotal,
  IFinalSettlementBatchResult,
  IFinalSettlementDetail,
  IGetScopeEmployeesParams,
  IJobLog,
  IListItemsParams,
  IListPaymentFilesParams,
  IListPayrollHistoryParams,
  IListPayslipsParams,
  IListRunsParams,
  IPaymentFile,
  IPaymentFileListItem,
  IPaymentFileMonthGroup,
  IPayrollBranch,
  IPayrollDepartment,
  IPayrollGroupScope,
  IPayrollHistoryEmployeePreview,
  IPayrollHistoryMeta,
  IPayrollHistoryMonth,
  IPayrollHistoryRun,
  IPayrollHistoryTotalByCurrency,
  IPayrollRun,
  IPayrollRunDetail,
  IPayrollRunItem,
  IPayrollRunItemDetail,
  IPayrollRunItemLine,
  IPayslipListItem,
  IPayslipPreview,
  IPeriodOption,
  IPeriodOptionsResponse,
  IReprocessHeldResult,
  IRunProgress,
  IRunScope,
  IScopeEmployee,
  IStatutoryLine,
  ITotalByCurrency,
  IUpdateItemRequest,
  IUpdateRunRequest,
} from "@/types/(company)/payrollRuns.interface";

// ─── API ───────────────────────────────────────────────────────────────────────

export const companyPayrollRunsApi = createApi({
  reducerPath: "companyPayrollRunsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "payrollRuns",
    "payrollRun",
    "payrollRunItems",
    "payrollRunItem",
    "payrollProgress",
    "payrollItemAuditLogs",
    "payrollRunAuditLogs",
    "payrollPeriodOptions",
  ],
  endpoints: (builder) => ({
    /**
     * A full payslip computed from the CURRENT configuration, without
     * creating a run or persisting anything — for checking that payroll
     * settings produce sane numbers before the first live run.
     *
     * `assumptions` comes back from the API and MUST be shown: the preview
     * assumes full attendance and skips overtime, penalties and mid-period
     * proration, because a hypothetical period has no attendance data. A
     * figure labelled "payslip" that quietly omits Loss of Pay would be
     * trusted for more than it can bear.
     */
    getPayslipPreview: builder.query<
      IApiResponse<IPayslipConfigPreview>,
      { employeeId: number; periodStart: string; periodEnd: string }
    >({
      query: ({ employeeId, periodStart, periodEnd }) => ({
        url: `/company/payroll/employees/${employeeId}/payslip-preview`,
        method: "GET",
        params: { periodStart, periodEnd },
      }),
    }),

    // Scope helpers
    listPayrollBranches: builder.query<
      IApiResponse<IPayrollBranch[]>,
      string | void
    >({
      query: (search) => ({
        url: "/company/payroll/runs/branches",
        method: "GET",
        params: search ? { search } : undefined,
      }),
    }),
    getBranchScopeOptions: builder.query<
      IApiResponse<IBranchScopeOptions>,
      { branchId: number; search?: string }
    >({
      query: ({ branchId, search }) => ({
        url: `/company/payroll/runs/branches/${branchId}/scope-options`,
        method: "GET",
        params: search ? { search } : undefined,
      }),
    }),
    getScopeEmployees: builder.query<
      IApiPaginatedResponse<IScopeEmployee[]>,
      IGetScopeEmployeesParams
    >({
      query: ({
        branchId,
        departmentIds,
        payrollGroupIds,
        userIds,
        excludedUserIds,
        search,
        page = 1,
        limit = 20,
      }) => ({
        url: "/company/payroll/runs/scope-employees",
        method: "GET",
        params: {
          branchId,
          ...(departmentIds?.length
            ? { departmentIds: departmentIds.join(",") }
            : {}),
          ...(payrollGroupIds?.length
            ? { payrollGroupIds: payrollGroupIds.join(",") }
            : {}),
          ...(userIds?.length ? { userIds: userIds.join(",") } : {}),
          ...(excludedUserIds?.length
            ? { excludedUserIds: excludedUserIds.join(",") }
            : {}),
          ...(search ? { search } : {}),
          page,
          limit,
        },
      }),
    }),
    getPeriodOptions: builder.query<
      IApiResponse<IPeriodOptionsResponse>,
      IPeriodOptionsParams
    >({
      query: ({ branchId, departmentIds, payrollGroupIds }) => ({
        url: "/company/payroll/runs/period-options",
        method: "GET",
        params: {
          branchId,
          // Passing the selected scope makes the period lock scope-aware — a
          // month is only "already run" once all scoped employees are covered.
          ...(departmentIds?.length
            ? { departmentIds: departmentIds.join(",") }
            : {}),
          ...(payrollGroupIds?.length
            ? { payrollGroupIds: payrollGroupIds.join(",") }
            : {}),
        },
      }),
      providesTags: (_r, _e, { branchId }) => [
        { type: "payrollPeriodOptions", id: branchId },
      ],
    }),

    // Run CRUD
    listRuns: builder.query<
      IApiPaginatedResponse<IPayrollRun[]>,
      IListRunsParams | void
    >({
      query: (params) => ({
        url: "/company/payroll/runs",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
          ...(params?.bucket ? { bucket: params.bucket } : {}),
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.periodFrom ? { periodFrom: params.periodFrom } : {}),
          ...(params?.periodTo ? { periodTo: params.periodTo } : {}),
        },
      }),
      providesTags: ["payrollRuns"],
    }),
    getRunDetail: builder.query<IApiResponse<IPayrollRunDetail>, number>({
      query: (id) => ({
        url: `/company/payroll/runs/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "payrollRun", id }],
    }),
    createRun: builder.mutation<
      IApiResponse<IPayrollRunDetail>,
      ICreateRunRequest
    >({
      query: (body) => ({
        url: "/company/payroll/runs",
        method: "POST",
        body,
      }),
      // A new run changes period coverage — refresh the (scope-aware) period
      // options so the just-run period reflects the new coverage immediately.
      // The id-less tag invalidates every cached scope variant.
      invalidatesTags: ["payrollRuns", "payrollPeriodOptions"],
    }),
    savePeriodOverride: builder.mutation<
      IApiResponse<{
        branchId: number;
        periodKey: string;
        payDate: string | null;
        payrollDate: string | null;
        name: string | null;
        notes: string | null;
      }>,
      {
        branchId: number;
        periodKey: string;
        payDate?: string;
        payrollDate?: string;
        name?: string;
        notes?: string;
      }
    >({
      query: (body) => ({
        url: "/company/payroll/runs/configure-period",
        method: "PUT",
        body: { ...body, saveAsDraft: true },
      }),
      invalidatesTags: (_r, _e, { branchId }) => [
        { type: "payrollPeriodOptions", id: branchId },
        "payrollRuns",
      ],
    }),
    updateRun: builder.mutation<
      IApiResponse<IPayrollRunDetail>,
      { id: number; data: IUpdateRunRequest }
    >({
      query: ({ id, data }) => ({
        url: `/company/payroll/runs/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "payrollRuns",
        { type: "payrollRun", id },
      ],
    }),
    startReview: builder.mutation<
      IApiResponse<{ id: number; status: string }>,
      number
    >({
      query: (id) => ({
        url: `/company/payroll/runs/${id}/start-review`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, id) => [
        "payrollRuns",
        { type: "payrollRun", id },
      ],
    }),
    // Permanently deletes a DRAFT / IN_REVIEW / FAILED run and releases what it
    // used (one-time deductions, overtime, reimbursements), so the period can be
    // generated again. Approved and processed runs are refused server-side.
    deleteRun: builder.mutation<
      IApiResponse<{ id: number; deleted: boolean }>,
      number
    >({
      query: (id) => ({
        url: `/company/payroll/runs/${id}/permanent`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        "payrollRuns",
        { type: "payrollRun", id },
      ],
    }),
    approveRun: builder.mutation<
      IApiResponse<{ id: number; status: string }>,
      number
    >({
      query: (id) => ({
        url: `/company/payroll/runs/${id}/approve`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, id) => [
        "payrollRuns",
        { type: "payrollRun", id },
      ],
    }),
    processRun: builder.mutation<
      IApiResponse<{ id: number; status: string }>,
      number
    >({
      query: (id) => ({
        url: `/company/payroll/runs/${id}/process`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, id) => [
        "payrollRuns",
        { type: "payrollRun", id },
      ],
    }),
    cancelRun: builder.mutation<
      IApiResponse<null>,
      { id: number; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `/company/payroll/runs/${id}`,
        method: "DELETE",
        body: reason ? { reason } : {},
      }),
      // Cancelling frees its employees, so their period becomes available again.
      invalidatesTags: ["payrollRuns", "payrollPeriodOptions"],
    }),

    // Items
    listItems: builder.query<
      IApiPaginatedResponse<IPayrollRunItem[]>,
      IListItemsParams
    >({
      query: ({
        runId,
        page = 1,
        limit = 20,
        search,
        status,
        departmentId,
        paymentMethod,
        paidStatus,
        holdSalary,
        excludeItemIds,
      }) => ({
        url: `/company/payroll/runs/${runId}/items`,
        method: "GET",
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(departmentId ? { departmentId } : {}),
          ...(paymentMethod ? { paymentMethod } : {}),
          ...(paidStatus ? { paidStatus } : {}),
          ...(holdSalary !== undefined
            ? { holdSalary: String(holdSalary) }
            : {}),
          ...(excludeItemIds ? { excludeItemIds } : {}),
        },
      }),
      providesTags: (_r, _e, { runId }) => [
        { type: "payrollRunItems", id: runId },
      ],
    }),
    getItemDetail: builder.query<
      IApiResponse<IPayrollRunItemDetail>,
      { runId: number; itemId: number }
    >({
      query: ({ runId, itemId }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}`,
        method: "GET",
      }),
      providesTags: (_r, _e, { itemId }) => [
        { type: "payrollRunItem", id: itemId },
      ],
    }),
    updateItem: builder.mutation<
      IApiResponse<IPayrollRunItemDetail>,
      { runId: number; itemId: number; data: IUpdateItemRequest }
    >({
      query: ({ runId, itemId, data }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { runId, itemId }) => [
        { type: "payrollRunItems", id: runId },
        { type: "payrollRunItem", id: itemId },
        { type: "payrollRun", id: runId },
        { type: "payrollItemAuditLogs", id: itemId },
        { type: "payrollRunAuditLogs", id: runId },
      ],
    }),
    getWpsExcluded: builder.query<IApiResponse<IWpsExcludedResponse>, number>({
      query: (runId) => ({
        url: `/company/payroll/runs/${runId}/wps-excluded`,
        method: "GET",
      }),
      providesTags: (_r, _e, runId) => [{ type: "payrollRunItems", id: runId }],
    }),
    updateItemBankDetails: builder.mutation<
      IApiResponse<IBankDetailsSaveResult>,
      {
        runId: number;
        itemId: number;
        data: {
          bankName?: string | null;
          accountNumber?: string | null;
          routingNumber?: string | null;
          wpsPersonId?: string | null;
        };
      }
    >({
      query: ({ runId, itemId, data }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}/bank-details`,
        method: "PATCH",
        body: data,
      }),
      // Deliberately does NOT invalidate the WPS-excluded list (payrollRunItems)
      // so a resolved row stays visible/selectable until the user regenerates.
      invalidatesTags: (_r, _e, { runId, itemId }) => [
        { type: "payrollRunItem", id: itemId },
        { type: "payrollRun", id: runId },
      ],
    }),
    updateItemAmounts: builder.mutation<
      IApiResponse<IAmountsSaveResult>,
      {
        runId: number;
        itemId: number;
        data: IUpdateItemRequest;
      }
    >({
      query: ({ runId, itemId, data }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}/amounts`,
        method: "PATCH",
        body: data,
      }),
      // Same as bank-details: keep the resolved row staged until regenerate.
      invalidatesTags: (_r, _e, { runId, itemId }) => [
        { type: "payrollRunItem", id: itemId },
        { type: "payrollRun", id: runId },
      ],
    }),
    getItemAuditLogs: builder.query<
      IApiResponse<IPayrollAuditLog[]>,
      { runId: number; itemId: number }
    >({
      query: ({ runId, itemId }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}/audit-logs`,
        method: "GET",
      }),
      providesTags: (_r, _e, { itemId }) => [
        { type: "payrollItemAuditLogs", id: itemId },
      ],
    }),
    getRunAuditLogs: builder.query<IApiResponse<IPayrollAuditLog[]>, number>({
      query: (runId) => ({
        url: `/company/payroll/runs/${runId}/audit-logs`,
        method: "GET",
      }),
      providesTags: (_r, _e, runId) => [
        { type: "payrollRunAuditLogs", id: runId },
      ],
    }),
    reviewItem: builder.mutation<
      IApiResponse<IPayrollRunItem>,
      { runId: number; itemId: number }
    >({
      query: ({ runId, itemId }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}/review`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, { runId, itemId }) => [
        { type: "payrollRunItems", id: runId },
        { type: "payrollRunItem", id: itemId },
        { type: "payrollItemAuditLogs", id: itemId },
        { type: "payrollRunAuditLogs", id: runId },
      ],
    }),
    holdItem: builder.mutation<
      IApiResponse<IPayrollRunItem>,
      { runId: number; itemId: number; reason: string }
    >({
      query: ({ runId, itemId, reason }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}/hold`,
        method: "PATCH",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { runId, itemId }) => [
        { type: "payrollRunItems", id: runId },
        { type: "payrollRunItem", id: itemId },
        { type: "payrollRun", id: runId },
        { type: "payrollItemAuditLogs", id: itemId },
        { type: "payrollRunAuditLogs", id: runId },
      ],
    }),
    /** Put an excluded employee back into a run in review (D3). */
    reincludeItem: builder.mutation<
      IApiResponse<IPayrollRunItem>,
      { runId: number; itemId: number; reason: string }
    >({
      query: ({ runId, itemId, reason }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}/reinclude`,
        method: "PATCH",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { runId, itemId }) => [
        { type: "payrollRunItems", id: runId },
        { type: "payrollRunItem", id: itemId },
        { type: "payrollRun", id: runId },
        { type: "payrollItemAuditLogs", id: itemId },
        { type: "payrollRunAuditLogs", id: runId },
      ],
    }),
    releaseItem: builder.mutation<
      IApiResponse<IPayrollRunItem>,
      { runId: number; itemId: number }
    >({
      query: ({ runId, itemId }) => ({
        url: `/company/payroll/runs/${runId}/items/${itemId}/release`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, { runId, itemId }) => [
        { type: "payrollRunItems", id: runId },
        { type: "payrollRunItem", id: itemId },
        { type: "payrollRun", id: runId },
        { type: "payrollItemAuditLogs", id: itemId },
        { type: "payrollRunAuditLogs", id: runId },
      ],
    }),
    reprocessHeld: builder.mutation<
      IApiResponse<IReprocessHeldResult>,
      { runId: number; itemIds?: number[]; description?: string }
    >({
      query: ({ runId, itemIds, description }) => ({
        url: `/company/payroll/runs/${runId}/reprocess-held`,
        method: "POST",
        body: {
          ...(itemIds?.length ? { itemIds } : {}),
          ...(description ? { description } : {}),
        },
      }),
      invalidatesTags: (_r, _e, { runId }) => [
        { type: "payrollRun", id: runId },
        { type: "payrollRunItems", id: runId },
        { type: "payrollRunAuditLogs", id: runId },
      ],
    }),

    // Async generation
    getProgress: builder.query<IApiResponse<IRunProgress>, number>({
      query: (id) => ({
        url: `/company/payroll/runs/${id}/progress`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "payrollProgress", id }],
    }),
    retryFailed: builder.mutation<
      IApiResponse<{ runId: number; retried: number }>,
      number
    >({
      query: (id) => ({
        url: `/company/payroll/runs/${id}/retry-failed`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "payrollRuns",
        { type: "payrollRun", id },
      ],
    }),
    getJobLogs: builder.query<
      IApiPaginatedResponse<IJobLog[]>,
      {
        runId: number;
        page?: number;
        limit?: number;
        event?: string;
        userId?: number;
      }
    >({
      query: ({ runId, page = 1, limit = 50, event, userId }) => ({
        url: `/company/payroll/runs/${runId}/job-logs`,
        method: "GET",
        params: {
          page,
          limit,
          ...(event ? { event } : {}),
          ...(userId ? { userId } : {}),
        },
      }),
    }),
    listPaymentFiles: builder.query<
      {
        success: boolean;
        statusCode: number;
        message: string;
        data: IPaymentFileMonthGroup[];
      },
      IListPaymentFilesParams | void
    >({
      query: (params) => ({
        url: "/company/payroll/payment-files",
        method: "GET",
        params: {
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.periodFrom ? { periodFrom: params.periodFrom } : {}),
          ...(params?.periodTo ? { periodTo: params.periodTo } : {}),
        },
      }),
    }),
    getPaymentFiles: builder.query<
      IApiResponse<{ runId: number; data: IPaymentFile[] }>,
      number
    >({
      query: (runId) => ({
        url: `/company/payroll/runs/${runId}/payment-files`,
        method: "GET",
      }),
      providesTags: (_r, _e, runId) => [{ type: "payrollRun", id: runId }],
    }),
    regeneratePaymentFiles: builder.mutation<
      IApiResponse<unknown>,
      { runId: number; description?: string }
    >({
      query: ({ runId, description }) => ({
        url: `/company/payroll/runs/${runId}/regenerate-payment-files`,
        method: "POST",
        body: description ? { description } : {},
      }),
      invalidatesTags: (_r, _e, { runId }) => [
        { type: "payrollRun", id: runId },
        { type: "payrollRunItems", id: runId },
        "payrollRuns",
      ],
    }),
    listPayrollHistory: builder.query<
      {
        success: boolean;
        statusCode: number;
        message: string;
        data: IPayrollHistoryMonth[];
        meta: IPayrollHistoryMeta;
      },
      IListPayrollHistoryParams | void
    >({
      query: (params) => ({
        url: "/company/payroll/runs/history",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 10,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.departmentId
            ? { departmentId: params.departmentId }
            : {}),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.periodFrom ? { periodFrom: params.periodFrom } : {}),
          ...(params?.periodTo ? { periodTo: params.periodTo } : {}),
        },
      }),
      providesTags: ["payrollRuns"],
    }),
    listPayslips: builder.query<
      IApiPaginatedResponse<IPayslipListItem[]>,
      IListPayslipsParams | void
    >({
      query: (params) => ({
        url: "/company/payroll/payslips",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.departmentId
            ? { departmentId: params.departmentId }
            : {}),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.periodFrom ? { periodFrom: params.periodFrom } : {}),
          ...(params?.periodTo ? { periodTo: params.periodTo } : {}),
          ...(params?.runId ? { runId: params.runId } : {}),
        },
      }),
    }),
    getPayslipStats: builder.query<
      IApiResponse<IPayslipStats>,
      IListPayslipsParams | void
    >({
      query: (params) => ({
        url: "/company/payroll/payslips/stats",
        method: "GET",
        params: {
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.departmentId
            ? { departmentId: params.departmentId }
            : {}),
          ...(params?.periodFrom ? { periodFrom: params.periodFrom } : {}),
          ...(params?.periodTo ? { periodTo: params.periodTo } : {}),
          ...(params?.runId ? { runId: params.runId } : {}),
        },
      }),
      providesTags: ["payrollRuns"],
    }),
    getFinalSettlementStats: builder.query<
      IApiResponse<IPayslipStats>,
      IListPayslipsParams | void
    >({
      query: (params) => ({
        url: "/company/payroll/final-settlements/stats",
        method: "GET",
        params: {
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.departmentId
            ? { departmentId: params.departmentId }
            : {}),
          ...(params?.periodFrom ? { periodFrom: params.periodFrom } : {}),
          ...(params?.periodTo ? { periodTo: params.periodTo } : {}),
          ...(params?.runId ? { runId: params.runId } : {}),
        },
      }),
      providesTags: ["payrollRuns"],
    }),
    listFinalSettlements: builder.query<
      IApiPaginatedResponse<IPayslipListItem[]>,
      IListPayslipsParams | void
    >({
      query: (params) => ({
        url: "/company/payroll/final-settlements",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.departmentId
            ? { departmentId: params.departmentId }
            : {}),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.periodFrom ? { periodFrom: params.periodFrom } : {}),
          ...(params?.periodTo ? { periodTo: params.periodTo } : {}),
          ...(params?.runId ? { runId: params.runId } : {}),
        },
      }),
      providesTags: ["payrollRuns"],
    }),
    getFinalSettlementDetail: builder.query<
      IApiResponse<IFinalSettlementDetail>,
      number
    >({
      query: (id) => ({
        url: `/company/payroll/final-settlements/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "payrollRun", id }],
    }),
    /** DRAFT → APPROVED, by someone other than the submitter (B27). */
    approveFinalSettlement: builder.mutation<
      IApiResponse<{ runId: number; status: string }>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/company/payroll/final-settlements/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "payrollRun", id },
        "payrollRuns",
      ],
    }),
    markFinalSettlementPaid: builder.mutation<
      IApiResponse<{ runId: number; markedCount: number; itemIds: number[] }>,
      { id: number; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/company/payroll/final-settlements/${id}/mark-paid`,
        method: "POST",
        body: { note },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "payrollRun", id },
        "payrollRuns",
      ],
    }),
    generateFinalSettlementWps: builder.mutation<
      IApiResponse<IFinalSettlementBatchResult>,
      { runIds: number[]; description?: string }
    >({
      query: (body) => ({
        url: "/company/payroll/final-settlements/generate-wps",
        method: "POST",
        body,
      }),
      invalidatesTags: ["payrollRuns"],
    }),
    processPendingFinalSettlements: builder.mutation<
      IApiResponse<IFinalSettlementBatchResult>,
      { branchId?: number } | void
    >({
      query: (body) => ({
        url: "/company/payroll/final-settlements/process-pending",
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: ["payrollRuns"],
    }),
    getCashDisbursements: builder.query<
      IApiResponse<{ runId: number; data: ICashDisbursementItem[] }>,
      number
    >({
      query: (runId) => ({
        url: `/company/payroll/runs/${runId}/cash-disbursements`,
        method: "GET",
      }),
      providesTags: (_r, _e, runId) => [{ type: "payrollRun", id: runId }],
    }),
    getBankTransfers: builder.query<
      IApiResponse<{ runId: number; data: IBankTransferItem[] }>,
      number
    >({
      query: (runId) => ({
        url: `/company/payroll/runs/${runId}/bank-transfers`,
        method: "GET",
      }),
      providesTags: (_r, _e, runId) => [{ type: "payrollRun", id: runId }],
    }),
    markItemsPaid: builder.mutation<
      IApiResponse<{ runId: number; paidCount: number }>,
      { runId: number; itemIds?: number[]; all?: boolean; note?: string }
    >({
      query: ({ runId, itemIds, all, note }) => ({
        url: `/company/payroll/runs/${runId}/items/mark-paid`,
        method: "PATCH",
        body: {
          ...(all ? { all: true } : { itemIds: itemIds ?? [] }),
          ...(note ? { note } : {}),
        },
      }),
      invalidatesTags: (_r, _e, { runId }) => [
        { type: "payrollRun", id: runId },
        { type: "payrollRunItems", id: runId },
      ],
    }),
    requestCashSignatures: builder.mutation<
      IApiResponse<{ jobId: string; total: number; kind: "cash" }>,
      { runId: number; itemIds: number[] }
    >({
      query: ({ runId, itemIds }) => ({
        url: `/company/payroll/runs/${runId}/request-cash-signatures`,
        method: "POST",
        body: { itemIds },
      }),
    }),
    requestReceiptSignatures: builder.mutation<
      IApiResponse<{ jobId: string; total: number; kind: "receipt" }>,
      { runId: number; itemIds?: number[] }
    >({
      query: ({ runId, itemIds }) => ({
        url: `/company/payroll/runs/${runId}/request-receipt-signatures`,
        method: "POST",
        body: { itemIds },
      }),
      invalidatesTags: (_r, _e, { runId }) => [
        { type: "payrollRunItems", id: runId },
      ],
    }),
  }),
});

export const {
  useDeleteRunMutation,
  useGetPayslipPreviewQuery,
  useListPayrollBranchesQuery,
  useGetBranchScopeOptionsQuery,
  useGetScopeEmployeesQuery,
  useGetPeriodOptionsQuery,
  useListRunsQuery,
  useGetRunDetailQuery,
  useCreateRunMutation,
  useSavePeriodOverrideMutation,
  useUpdateRunMutation,
  useStartReviewMutation,
  useApproveRunMutation,
  useProcessRunMutation,
  useCancelRunMutation,
  useListItemsQuery,
  useLazyListItemsQuery,
  useGetItemDetailQuery,
  useGetWpsExcludedQuery,
  useUpdateItemBankDetailsMutation,
  useUpdateItemAmountsMutation,
  useUpdateItemMutation,
  useReviewItemMutation,
  useHoldItemMutation,
  useReleaseItemMutation,
  useReincludeItemMutation,
  useReprocessHeldMutation,
  useGetItemAuditLogsQuery,
  useGetRunAuditLogsQuery,
  useGetProgressQuery,
  useRetryFailedMutation,
  useGetJobLogsQuery,
  useGetPaymentFilesQuery,
  useRegeneratePaymentFilesMutation,
  useListPaymentFilesQuery,
  useGetCashDisbursementsQuery,
  useGetBankTransfersQuery,
  useMarkItemsPaidMutation,
  useListPayslipsQuery,
  useGetPayslipStatsQuery,
  useGetFinalSettlementStatsQuery,
  useListFinalSettlementsQuery,
  useGetFinalSettlementDetailQuery,
  useApproveFinalSettlementMutation,
  useMarkFinalSettlementPaidMutation,
  useGenerateFinalSettlementWpsMutation,
  useProcessPendingFinalSettlementsMutation,
  useListPayrollHistoryQuery,
  useRequestCashSignaturesMutation,
  useRequestReceiptSignaturesMutation,
} = companyPayrollRunsApi;
